import { defineStore } from "pinia";
import {
  searchCompany,
  searchEmployees,
  searchEmployeeByLinkedin,
} from "@/api/company";
import { checkLogin } from "@/api/login";
import {
  // getUserPermission,
  getSubscriptionInfo,
  getSubscriptionRenewStatus,
  type SubscriptionRenewStatusType,
} from "@/api/user";
import { useSubscriptionStore } from "@/stores/subscription";

export interface SidebarCompanyInfo {
  id: string;
  name: string;
  lid: string;
  url: string;
  description: string;
  numberOfEmployees: number;
  website: string;
  pictureUrl: string;
  type: string;
  companyResult: {
    name: string;
    url: string;
    description: string;
    address: {
      streetAddress: string;
      addressLocality: string;
      addressRegion: string;
      postalCode: string;
      addressCountry: string;
      addressCountryCode: string;
      rawAddress: string;
    };
    // numberOfEmployees: string;
    industry: string;
    website: string;
    specialties: Array<{
      value: string;
    }>;
    deleted: boolean;
    foundedYear: string;
    pictureUrl: string;
    estimatedRevenuRange: {
      estimatedMaxRevenue: {
        amount: number;
        unit: string;
        currency: string;
      };
      estimatedMinRevenue: {
        amount: number;
        unit: string;
        currency: string;
      };
    };
    annualRevenue: string;
    totalFunding: string;
    twitterUrl: string;
    facebookUrl: string;
    fundingEvents: Array<{
      id: string;
      date: string;
      type: string;
      investors: string;
      amount: string;
      currency: string;
      news_url: string;
    }>;
  };
  universalName: string;
}

export interface SidebarEmployee {
  // 以下字段来自API
  fullName: string;
  title: string;
  companyName: string;
  profilePictureUrl: string;
  summary: string;
  country: string;
  location: string;
  skills: string[];
  experience: {
    title: string;
    companyName: string;
    startDateYear: number;
    startDateMonth: number;
    endDateYear: number | null;
    endDateMonth: number | null;
    isCurrent: boolean;
    linkedinUrl: string;
    logoUrl?: string;
  }[];
  education: {
    fieldOfStudy: string;
    startDateYear: number;
    endDateYear: number | null;
    degree: string;
    schoolName: string;
    url?: string;
  }[];
  linkedinUrl: string;
  publicId: string;
  contactDetailVO: {
    emailState: import("@/types/contact").ContactState;
    emailList: {
      name: string;
      email: string;
      source: string;
      usedTime: string;
      valid: boolean;
      used: boolean;
    }[];
    phoneState: import("@/types/contact").ContactState;
    phoneList: {
      name: string;
      phone: string;
      source: string;
      valid: boolean;
    }[];
  };
}

export interface SidebarSubscription {
  quota: number;
  remainingQuota: number;
  packageInfo: {
    id: string;
    name: string;
    price: number;
  };
  renewFail: boolean;
  renewFailMissingPaymentMethod?: boolean;
  isActive: boolean;
  isOneTimePay?: boolean;
}

function capitalize(str: string): string {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export const useSidebarStore = defineStore("sidebar", {
  state: () => ({
    domain: "" as string,
    loading: false,
    companyLoading: false,

    isLoggedIn: false,
    loginChecked: false,

    userInfo: null as any,
    subscription: null as SidebarSubscription | null,
    renewStatus: null as SubscriptionRenewStatusType | null,

    company: null as SidebarCompanyInfo | null,
    companyNotFound: false,

    allEmployees: [] as SidebarEmployee[],
    dmEmployees: [] as SidebarEmployee[],
    // allEmployeesCount: 0,
    // dmEmployeesCount: 0,
    employeesSearched: false,

    activeTab: "company" as "company" | "employees",
    employeeTab: "dm" as "all" | "dm",

    selectedEmployee: null as SidebarEmployee | null,
    linkedinEmployeeLoading: false,
    linkedinEmployee: null as SidebarEmployee | null,

    showEmployeeBadge: true,
    showLock: false,
    isFreePackage: false,
    fetchedTypes: {} as Record<number, boolean>,
    loadingTypes: {} as Record<number, boolean>,
    scrollPositions: {} as Record<string, number>,
  }),
  getters: {
    employeesLoading: (state) =>
      Object.values(state.loadingTypes).some(Boolean),
    isSubscribed: (state) => state.subscription?.isActive ?? false,
    isUnlimited: (state) =>
      state.subscription?.packageInfo?.name
        ?.toLowerCase()
        .includes("unlimited") ?? false,
    remainingCredits: (state) => state.subscription?.remainingQuota ?? 0,
    hasRenewFailMissingPaymentMethod: (state) =>
      state.renewStatus?.renewFailMissingPaymentMethod ?? false,
    hasPaymentFailed: (state) => state.subscription?.renewFail ?? false,
    totalCredits: (state) => state.subscription?.quota ?? 0,

    // 每月可查看联系人总数
    // monthlyViewLimit: (state): number => {
    //   return state.subscription?.isActive ? 500 : 100;
    // },
    // 每个企业每个tab的最大可查看数
    perCompanyTabLimit: (state): number => {
      return state.subscription?.isActive ? 25 : 10;
    },
  },
  actions: {
    setDomain(domain: string) {
      if (this.domain === domain) return;
      this.domain = domain;
      this.allEmployees = [];
      this.dmEmployees = [];
      this.fetchedTypes = {};
      this.loadingTypes = {};
      this.employeesSearched = false;
      this.company = null;
      this.companyNotFound = false;
    },

    async checkLoginStatus() {
      try {
        const { code, data } = await checkLogin({ targetUrl: document.URL });
        if (code === 200) {
          this.isLoggedIn = data?.login ?? false;
        }
      } catch (e) {
        console.error("checkLogin error", e);
      }
      this.loginChecked = true;
    },

    // async fetchUserInfo() {
    //   try {
    //     const { code, data } = await getUserPermission({});
    //     if (code === 200) {
    //       this.userInfo = data;
    //     }
    //   } catch (e) {
    //     console.error("fetchUserInfo error", e);
    //   }
    // },

    async fetchSubscription() {
      try {
        const [{ code, data }] = await Promise.all([
          getSubscriptionInfo(),
          this.fetchRenewStatus(),
        ]);
        if (code === 200) {
          this.subscription = data as any;
        }
      } catch (e) {
        console.error("fetchSubscription error", e);
      }
    },

    async fetchRenewStatus() {
      try {
        const { code, data } = await getSubscriptionRenewStatus();
        if (code === 200 && data) {
          this.renewStatus = data;
          return data;
        }
      } catch (e) {
        console.error("fetchRenewStatus error", e);
      }
      return null;
    },

    async fetchCompany() {
      if (!this.domain) return;
      this.companyLoading = true;
      this.companyNotFound = false;
      try {
        const { code, data } = await searchCompany({
          domain: this.domain,
          force_search: false,
        });
        if (code === 200 && data) {
          this.company = data as any;
        } else {
          this.companyNotFound = true;
        }
      } catch (e) {
        this.companyNotFound = true;
      }
      this.companyLoading = false;
    },

    async fetchEmployees(type?: 1 | 2) {
      if (!this.domain || this.companyNotFound) return;

      const fetchType = type || (this.employeeTab === "dm" ? 2 : 1);

      // 如果已经加载过该类型，或正在加载该类型，直接返回
      if (this.fetchedTypes[fetchType] || this.loadingTypes[fetchType]) return;

      // 如果需要 universal_name 但公司信息还没加载完，直接返回
      // index.vue 的 onMounted 会在 init() 之后再次触发 fetchEmployees
      if (!this.company) return;

      this.loadingTypes[fetchType] = true;
      try {
        const params: any = {
          domain: this.domain,
          type: fetchType,
          universal_name: this.company.universalName,
        };
        const { code, data } = await searchEmployees(params);
        if (code === 200 && data) {
          const rawEmployees = data.employees || [];
          const employees: SidebarEmployee[] = rawEmployees.map((emp: any) => ({
            ...emp,
          }));
          if (fetchType === 1) {
            this.allEmployees = employees;
          } else {
            this.dmEmployees = employees;
          }
          this.fetchedTypes[fetchType] = true;
          this.showLock = data.showLock ?? false;
          this.isFreePackage = data.isFreePackage ?? false;
          this.employeesSearched = true;
        }
      } catch (e) {
        console.error("fetchEmployees error", e);
      } finally {
        this.loadingTypes[fetchType] = false;
      }
    },

    async fetchEmployeeByLinkedin(linkedin_url: string) {
      if (!linkedin_url) return;
      this.linkedinEmployeeLoading = true;
      this.linkedinEmployee = null;
      try {
        const { code, data } = await searchEmployeeByLinkedin({ linkedin_url });
        if (code === 200 && data) {
          this.linkedinEmployee = data.employees[0] as SidebarEmployee;
        }
      } catch (e) {
        console.error("fetchEmployeeByLinkedin error", e);
      } finally {
        this.linkedinEmployeeLoading = false;
      }
    },

    async initStandalone() {
      this.loading = true;
      try {
        await this.checkLoginStatus();
        if (this.isLoggedIn) {
          const subscriptionStore = useSubscriptionStore();
          await Promise.all([
            this.fetchSubscription(),
            subscriptionStore.updateCreditCosts(),
          ]);
        }
      } finally {
        this.loading = false;
      }
    },

    async init() {
      this.loading = true;
      try {
        await this.checkLoginStatus();
        if (this.isLoggedIn) {
          const subscriptionStore = useSubscriptionStore();
          await Promise.all([
            // this.fetchUserInfo(),
            this.fetchSubscription(),
            subscriptionStore.updateCreditCosts(),
          ]);
        }
        await this.fetchCompany();
      } finally {
        this.loading = false;
      }
    },

    setActiveTab(tab: "company" | "employees") {
      this.activeTab = tab;
      if (tab === "employees") {
        this.showEmployeeBadge = false;
      }
    },

    setEmployeeTab(tab: "all" | "dm") {
      this.employeeTab = tab;
      if (tab === "all" && !this.fetchedTypes[1]) {
        this.fetchEmployees(1);
      } else if (tab === "dm" && !this.fetchedTypes[2]) {
        this.fetchEmployees(2);
      }
    },

    selectEmployee(employee: SidebarEmployee | null) {
      this.selectedEmployee = employee;
    },

    // 更新指定员工的联系方式信息 (email/phone 查找后回写)
    updateEmployeeContact(
      publicId: string,
      patch: Partial<SidebarEmployee["contactDetailVO"]>,
    ) {
      const update = (list: SidebarEmployee[]) => {
        const emp = list.find((e) => e.publicId === publicId);
        if (emp) {
          emp.contactDetailVO = { ...emp.contactDetailVO, ...patch };
        }
      };
      update(this.allEmployees);
      update(this.dmEmployees);

      // 同时更新独立的领英员工数据
      if (
        this.linkedinEmployee &&
        this.linkedinEmployee.publicId === publicId
      ) {
        this.linkedinEmployee.contactDetailVO = {
          ...this.linkedinEmployee.contactDetailVO,
          ...patch,
        };
      }
    },

    setScrollPosition(path: string, top: number) {
      this.scrollPositions[path] = top;
    },
  },
});
